import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);
dayjs.extend(duration);

export { dayjs };
export const addDays = (start: Date, n: number): Date => {
  const result = new Date(start);
  const startDate = start.getDate();
  result.setDate(startDate + n);
  return result;
};

export const thirty_one_days_in_the_future = addDays(new Date(), 31);

const LOCAL_DATETIME_TZ_FORMATTER = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: true,
  timeZoneName: 'short'
});

/**
 * Returns the local timezone abbreviation for a date (e.g. "PST", "EST").
 * Used when displaying gitless session timestamps.
 */
export const getLocalTimezoneAbbreviation = (date: Date): string => {
  const parts = LOCAL_DATETIME_TZ_FORMATTER.formatToParts(date);
  return parts.find(p => p.type === 'timeZoneName')?.value ?? '';
};

/**
 * Formats a date as "MM/DD/YYYY hh:mm:ss A" with local timezone abbreviation.
 * Uses a single Intl formatter (local timezone) so the time and timezone always match.
 */
export const formatLocalDateTimeWithTimezone = (date: Date): string => {
  // Validate date parameter
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return 'Invalid date';
  }

  const parts = LOCAL_DATETIME_TZ_FORMATTER.formatToParts(date);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  const month = get('month');
  const day = get('day');
  const year = get('year');
  const hour = get('hour');
  const minute = get('minute');
  const second = get('second');
  const dayPeriod = get('dayPeriod').toUpperCase();
  const timeZoneName = get('timeZoneName');

  if (!month || !day || !year) {
    // Use locale-aware fallback instead of hardcoded MM/DD/YYYY format
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
  }
  return `${month}/${day}/${year} ${hour}:${minute}:${second} ${dayPeriod} ${timeZoneName}`;
};

/**
 * Formats a date as a human-readable long-form string using the system locale.
 * e.g. "January 15, 2024 1:00 PM"
 */
export const formatLongDateTime = (date: Date | string): string => {
  const parsed = typeof date === 'string' ? new Date(date) : date;

  if (!parsed || !(parsed instanceof Date) || isNaN(parsed.getTime())) {
    return 'Invalid date';
  }

  const datePart = parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const timePart = parsed.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  });
  return `${datePart} ${timePart}`;
};

export const getRelativeTime = (date: Date | null) => {
  return date
    ? dayjs(
        new Intl.DateTimeFormat('en', {
          dateStyle: 'short',
          timeStyle: 'short'
        }).format(date)
      ).fromNow()
    : null;
};
